import { getCurrentLanguage } from 'utils/current-language'
import cookieService from './cookies'

interface Cookiebot {
  // https://www.cookiebot.com/en/developer/#h-properties
  consent: {
    necessary: boolean
    preferences: boolean
    statistics: boolean
    marketing: boolean
    method: 'implied' | 'explicit' | null
  }
  bulkconsent: {
    preferences: boolean
    statistics: boolean
    marketing: boolean
    ticket: string
    utc: number
  }
  consented: boolean
  bulkConsentEnabled: boolean
  consentID: string
  consentUTC: {
    getTime: () => string
  }
  // https://www.cookiebot.com/en/developer/#h-methods
  // we wont use withdraw() method for reject all cookies because for us preferences cookies are necessary
  // and thats why we should use submitCustomConsent() method over withdraw() method
  submitConsent: () => void
  submitCustomConsent: (preferences: boolean, statistics: boolean, marketing: boolean) => void
  renew: () => void
}

declare global {
  interface Window {
    // object from Cookiebot script
    Cookiebot: Cookiebot
    cookieConsent: object
  }
}

const cookieConsent = {
  // script tag
  $cookiebotScript: document.querySelector<HTMLScriptElement>('#Cookiebot'),
  // containers
  $bannerContainer: document.querySelector<HTMLDivElement>('#cookiebanner__container'),
  $banner: document.querySelector<HTMLElement>('#cookiebanner'),
  $dialog: document.querySelector<HTMLElement>('#cookiedetails'),
  $backdrop: document.querySelector<HTMLDivElement>('.cookiebanner__backdrop'),
  // widget containers
  $widgetContainer: document.querySelector<HTMLElement>('#cookiewidget-container'),
  $widgetDialog: document.querySelector<HTMLElement>('#cookiewidget-dialog'),
  // buttons
  $acceptAllButton: document.querySelector<HTMLButtonElement>('#banner-accept-all-button'),
  $acceptAllDialogButton: document.querySelector<HTMLButtonElement>('#details-accept-all-button'),
  $openDetailsButton: document.querySelector<HTMLButtonElement>('#banner-open-details-button'),
  $acceptSelectedButton: document.querySelector<HTMLButtonElement>('#details-accept-selected-button'),
  $rejectAllButton: document.querySelector<HTMLButtonElement>('#details-reject-all-button'),
  // widget buttons
  $widgetButton: document.querySelector<HTMLButtonElement>('#cookiewidget'),
  $widgetCloseButton: document.querySelector<HTMLButtonElement>('#widget-close-buttton'),
  $widgetRejectAllButton: document.querySelector<HTMLButtonElement>('#widget-reject-all-button'),
  $widgetChangeConsentButton: document.querySelector<HTMLButtonElement>('#widget-change-consent-button'),
  // switchers
  $switchers: document.querySelectorAll<HTMLDivElement>('.manage-cookies__switcher'),
  $analyticalSwitcher: document.querySelector<HTMLDivElement>('#analytical-switcher'),
  $marketingSwitcher: document.querySelector<HTMLDivElement>('#marketing-switcher'),
  // widget icons
  $widgetStatisticsIcon: document.querySelector<HTMLLIElement>('#cookiewidget-statistics-icon'),
  $widgetMarketingIcon: document.querySelector<HTMLLIElement>('#cookiewidget-marketing-icon'),
  // widget details
  $widgetConsentDate: document.querySelector<HTMLParagraphElement>('#cookiewidget-consent-date'),
  $widgetConsentID: document.querySelector<HTMLParagraphElement>('#cookiewidget-consent-id'),
  // states
  areSetBannerEventListeners: false,
  areSetWidgetEventListeners: false,
  isSetBannerDisplayHandler: false,
  isTriggeredWidgetRejectAll: false,
  // utils
  consent: window.Cookiebot ? (window.Cookiebot.bulkconsent ?? window.Cookiebot.consent) : undefined,
  consentUTC: window.Cookiebot
    ? (window.Cookiebot.bulkconsent?.utc ?? window.Cookiebot.consentUTC?.getTime())
    : undefined,
  consentID: window.Cookiebot
    ? Number(window.Cookiebot.bulkconsent?.ticket)
      ? window.Cookiebot.bulkconsent.ticket
      : window.Cookiebot.consentID
    : undefined,
  forcedInteractionOptions: cookieService.getItem('cookie_force_interaction'),

  setVariables(): void {
    const { Cookiebot } = window
    if (!Cookiebot) return

    cookieConsent.consent = Cookiebot.bulkconsent || Cookiebot.consent
    cookieConsent.consentUTC = Cookiebot.bulkconsent?.utc || Cookiebot.consentUTC?.getTime()
    cookieConsent.consentID = Number(Cookiebot.bulkconsent?.ticket) ? Cookiebot.bulkconsent.ticket : Cookiebot.consentID
  },

  setBannerEventListeners(): void {
    cookieConsent.$acceptAllButton?.addEventListener('click', cookieConsent.handleAcceptAllButtonClick)
    cookieConsent.$acceptAllDialogButton?.addEventListener('click', cookieConsent.handleAcceptAllButtonClick)
    cookieConsent.$openDetailsButton?.addEventListener('click', cookieConsent.handleOpenDetailsButtonClick)
    cookieConsent.$acceptSelectedButton?.addEventListener('click', cookieConsent.handleAcceptSelectedButtonClick)
    cookieConsent.$rejectAllButton?.addEventListener('click', () => cookieConsent.handleRejectAllButtonClick(false))
    cookieConsent.$switchers.forEach((switcher) => {
      switcher.addEventListener('click', (e) => cookieConsent.handleSwitcherClick(e, switcher))
    })
    cookieConsent.areSetBannerEventListeners = true
  },

  setWidgetEventListeners(): void {
    cookieConsent.$widgetButton?.addEventListener('click', cookieConsent.handleWidgetClick)
    cookieConsent.$widgetCloseButton?.addEventListener('click', cookieConsent.handleWidgetCloseButtonClick)
    cookieConsent.$widgetRejectAllButton?.addEventListener('click', () =>
      cookieConsent.handleRejectAllButtonClick(true),
    )
    cookieConsent.$widgetChangeConsentButton?.addEventListener(
      'click',
      cookieConsent.handleWidgetChangeConsentButtonClick,
    )
    cookieConsent.areSetWidgetEventListeners = true
  },

  setSwitchers(): void {
    if (cookieConsent.$analyticalSwitcher) {
      cookieConsent.consent.statistics
        ? cookieConsent.$analyticalSwitcher.classList.add('active')
        : cookieConsent.$analyticalSwitcher.classList.remove('active')
    }
    if (cookieConsent.$marketingSwitcher) {
      cookieConsent.consent.marketing
        ? cookieConsent.$marketingSwitcher.classList.add('active')
        : cookieConsent.$marketingSwitcher.classList.remove('active')
    }
  },

  setWidgetIcons(): void {
    if (cookieConsent.$widgetStatisticsIcon) {
      cookieConsent.setWidgetIcon(cookieConsent.$widgetStatisticsIcon, cookieConsent.consent.statistics)
    }
    if (cookieConsent.$widgetMarketingIcon) {
      cookieConsent.setWidgetIcon(cookieConsent.$widgetMarketingIcon, cookieConsent.consent.marketing)
    }
  },

  setWidgetIcon($icon: HTMLLIElement, isApproved: boolean): void {
    const toAdd = isApproved ? 'icon-check' : 'icon-cancel'
    const toRemove = isApproved ? 'icon-cancel' : 'icon-check'
    $icon.classList.replace(toRemove, toAdd)
  },

  setWidgetDetails(): void {
    if (cookieConsent.$widgetConsentDate) cookieConsent.$widgetConsentDate.textContent = cookieConsent.getConsentDate()
    if (cookieConsent.$widgetConsentID) cookieConsent.$widgetConsentID.innerText = String(cookieConsent.consentID)
  },

  setIsForcedInteraction(): void {
    cookieConsent.forcedInteractionOptions = cookieService.getItem('cookie_force_interaction')
  },

  getConsentDate(): string {
    const lang = getCurrentLanguage()
    const options: Intl.DateTimeFormatOptions = {
      dateStyle: 'long',
      timeStyle: 'long',
      timeZone: 'Poland',
    }
    const date = new Date(cookieConsent.consentUTC)
    return date.toLocaleString(lang, options)
  },

  setBannerDisplayHandler(): void {
    // https://www.cookiebot.com/en/developer/#h-event-handling
    window.addEventListener('CookiebotOnDialogDisplay', cookieConsent.handleBannerDisplay)
    cookieConsent.isSetBannerDisplayHandler = true
  },

  handleOnLoad(): void {
    cookieConsent.handleForceInteraction()
    cookieConsent.setVariables()

    if (!window.Cookiebot?.consented) {
      !window.Cookiebot?.bulkconsent
        ? cookieConsent.handleBannerDisplay()
        : cookieConsent.handleAcceptSelectedButtonClick()
    } else {
      cookieConsent.handleWidgetDisplay()
    }
  },

  handleBannerDisplay(): void {
    if (cookieConsent.isTriggeredWidgetRejectAll) {
      window.Cookiebot?.submitCustomConsent(
        cookieConsent.consent.preferences,
        cookieConsent.consent.statistics,
        cookieConsent.consent.marketing,
      )
      cookieConsent.isTriggeredWidgetRejectAll = false
    } else {
      cookieConsent.setVariables()
      if (cookieConsent.$bannerContainer) cookieConsent.$bannerContainer.hidden = false
      if (!cookieConsent.areSetBannerEventListeners) cookieConsent.setBannerEventListeners()
      window.Cookiebot?.consented ? cookieConsent.showDialog() : cookieConsent.showBanner()
    }
  },

  handleCookiesAccept(): void {
    if (!cookieConsent.isSetBannerDisplayHandler) cookieConsent.setBannerDisplayHandler()
    if (window.Cookiebot?.consented) {
      if (cookieConsent.forcedInteractionOptions != 'inactive') {
        cookieService.setItem('cookie_force_interaction', 'inactive', 'None', cookieService.maximalCookieTTL)
      }
      cookieConsent.hideBanner()
      cookieConsent.hideDialog()
      cookieConsent.hideWidgetDialog()
      cookieConsent.hideBackdrop()
      cookieConsent.handleWidgetDisplay()
    } else {
      window.Cookiebot?.renew()
    }
  },

  handleWidgetDisplay(): void {
    cookieConsent.setVariables()
    cookieConsent.showWidget()
  },

  handleAcceptAllButtonClick(): void {
    window.Cookiebot?.submitConsent()
  },

  handleAcceptSelectedButtonClick(): void {
    cookieConsent.consent.preferences = true // preferences are necessary

    window.Cookiebot?.submitCustomConsent(
      cookieConsent.consent.preferences,
      cookieConsent.consent.statistics,
      cookieConsent.consent.marketing,
    )
  },

  handleRejectAllButtonClick(isTriggeredWidgetRejectAll: boolean): void {
    cookieConsent.isTriggeredWidgetRejectAll = isTriggeredWidgetRejectAll
    cookieConsent.consent.preferences = true // preferences are necessary
    cookieConsent.consent.statistics = false
    cookieConsent.consent.marketing = false

    isTriggeredWidgetRejectAll
      ? window.Cookiebot?.renew()
      : window.Cookiebot?.submitCustomConsent(
          cookieConsent.consent.preferences,
          cookieConsent.consent.statistics,
          cookieConsent.consent.marketing,
        )
  },

  handleOpenDetailsButtonClick(): void {
    cookieConsent.hideBanner()
    cookieConsent.showDialog()
  },

  handleSwitcherClick(e: Event, switcher: HTMLDivElement): void {
    e.stopPropagation()
    e.preventDefault()
    switcher.classList.toggle('active')

    switch (switcher.id) {
      case 'analytical-switcher':
        cookieConsent.consent.statistics = !cookieConsent.consent.statistics
        break
      case 'marketing-switcher':
        cookieConsent.consent.marketing = !cookieConsent.consent.marketing
    }
  },

  handleWidgetClick(): void {
    if (cookieConsent.$widgetDialog) cookieConsent.$widgetDialog.hidden = !cookieConsent.$widgetDialog.hidden
  },

  handleWidgetCloseButtonClick(): void {
    cookieConsent.hideWidgetDialog()
  },

  handleWidgetChangeConsentButtonClick(): void {
    cookieConsent.hideWidgetDialog()
    cookieConsent.hideWidget()
    window.Cookiebot?.renew()
  },

  handleForceInteraction(): void {
    cookieConsent.setIsForcedInteraction()

    switch (cookieConsent.forcedInteractionOptions) {
      case 'active':
        cookieConsent.showBackdrop()
        break
      case 'inactive':
        cookieConsent.hideBackdrop()
        break
      default:
        cookieConsent.showBackdrop()
        cookieService.setItem('cookie_force_interaction', 'active', 'None', cookieService.maximalCookieTTL)
    }
  },

  showDialog(): void {
    cookieConsent.setSwitchers()
    cookieConsent.$bannerContainer?.setAttribute('data-template', 'popup')
    cookieConsent.showBackdrop()
    if (cookieConsent.$dialog) cookieConsent.$dialog.hidden = false
  },

  hideDialog(): void {
    if (cookieConsent.$dialog) cookieConsent.$dialog.hidden = true
    cookieConsent.$bannerContainer?.setAttribute('data-template', 'banner')
  },

  showBanner(): void {
    if (cookieConsent.$banner) cookieConsent.$banner.hidden = false
  },

  hideBanner(): void {
    if (cookieConsent.$banner) cookieConsent.$banner.hidden = true
  },

  showWidgetDialog(): void {
    if (cookieConsent.$widgetDialog) cookieConsent.$widgetDialog.hidden = false
  },

  hideWidgetDialog(): void {
    if (cookieConsent.$widgetDialog) cookieConsent.$widgetDialog.hidden = true
  },

  showWidget(): void {
    cookieConsent.setWidgetIcons()
    cookieConsent.setWidgetDetails()
    if (!cookieConsent.areSetWidgetEventListeners) cookieConsent.setWidgetEventListeners()
    if (cookieConsent.$widgetContainer) cookieConsent.$widgetContainer.hidden = false
  },

  hideWidget(): void {
    if (cookieConsent.$widgetContainer) cookieConsent.$widgetContainer.hidden = true
  },

  showBackdrop(): void {
    if (cookieConsent.$backdrop) cookieConsent.$backdrop.hidden = false
  },

  hideBackdrop(): void {
    if (cookieConsent.$backdrop) cookieConsent.$backdrop.hidden = true
  },

  async waitForCookiebot(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Cookiebot did not load within 10 seconds'))
      }, 10000)

      const checkCookiebot = () => {
        if (window.Cookiebot?.consent) {
          clearTimeout(timeout)
          resolve()
        } else {
          setTimeout(checkCookiebot, 500)
        }
      }
      checkCookiebot()
    })
  },

  init(): void {
    // https://www.cookiebot.com/en/developer/#h-event-handling
    window.addEventListener('CookiebotOnAccept', cookieConsent.handleCookiesAccept)
    cookieConsent
      .waitForCookiebot()
      .then(() => cookieConsent.handleOnLoad())
      .catch((error) => console.error('Error waiting for Cookiebot:', error))
  },
}

window.cookieConsent = cookieConsent

export default cookieConsent
